import { useState } from "react";
import { Alert, Button, List, message, Modal, Tag, Upload } from "antd";
import { FileWordOutlined, InboxOutlined } from "@ant-design/icons";
import service from "../../api/FetchInterceptor";

const MULTIPART = { ...service.defaults.headers, "Content-Type": "multipart/form-data" };

/**
 * Import d'un manuel Word : 1) envoi du .docx → aperçu des parties (rien n'est enregistré),
 * 2) confirmation → les parties sont ajoutées à la suite des existantes.
 */
const ManualImport = ({ formationId, open, setOpen, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const reset = () => {
        setFile(null);
        setPreview(null);
        setError(null);
        setLoading(false);
    };

    const close = () => {
        if (loading) return;
        reset();
        setOpen(false);
    };

    const send = (selected, confirm) => {
        const data = new FormData();
        data.append("file", selected);
        if (confirm) data.append("confirm", "1");

        setLoading(true);
        setError(null);

        return service({
            method: "POST",
            url: `admin/formations/${formationId}/import-manual`,
            data,
            headers: MULTIPART,
            timeout: 120000,
        })
            .catch((e) => {
                const errors = e?.response?.data?.errors;
                setError(errors ? Object.values(errors).flat()[0] : e?.response?.data?.message ?? "Le fichier n'a pas pu être analysé.");
                return null;
            })
            .finally(() => setLoading(false));
    };

    const analyse = (selected) => {
        setFile(selected);
        setPreview(null);
        send(selected, false).then((data) => data && setPreview(data));
        return false; // Pas d'upload automatique par antd.
    };

    const confirmImport = () => {
        send(file, true).then((data) => {
            if (!data) return;
            message.success(`${data.created} partie${data.created > 1 ? "s" : ""} importée${data.created > 1 ? "s" : ""} avec ${data.images_count} image${data.images_count > 1 ? "s" : ""}.`);
            reset();
            setOpen(false);
            onSuccess?.();
        });
    };

    const count = preview?.parts?.length ?? 0;

    return (
        <Modal
            title="Importer un manuel Word"
            open={open}
            onCancel={close}
            width={720}
            maskClosable={!loading}
            footer={
                preview
                    ? [
                          <Button key="other" onClick={reset} disabled={loading}>Choisir un autre fichier</Button>,
                          <Button key="import" type="primary" loading={loading} onClick={confirmImport}>
                              Importer ces {count} partie{count > 1 ? "s" : ""}
                          </Button>,
                      ]
                    : [<Button key="close" onClick={close} disabled={loading}>Fermer</Button>]
            }
        >
            {!preview && (
                <>
                    <p className="text-sm text-gray-600">
                        Chaque <strong>Titre 1</strong> du document devient une partie. Les titres 2 et 3 deviennent des
                        intertitres ; le gras, les listes et les images sont conservés. Le sommaire Word est ignoré.
                    </p>
                    <Upload.Dragger
                        accept=".docx"
                        multiple={false}
                        showUploadList={false}
                        beforeUpload={analyse}
                        disabled={loading}
                    >
                        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                        <p className="ant-upload-text">{loading ? "Analyse du document…" : "Cliquez ou déposez le fichier .docx ici"}</p>
                        <p className="ant-upload-hint">Rien n'est enregistré avant votre confirmation.</p>
                    </Upload.Dragger>
                </>
            )}

            {error && <Alert className="mt-4" type="error" showIcon message={error} />}

            {preview && (
                <>
                    <Alert
                        type="info"
                        showIcon
                        icon={<FileWordOutlined />}
                        message={`${file?.name} : ${count} partie${count > 1 ? "s" : ""}, ${preview.images_count} image${preview.images_count > 1 ? "s" : ""}`}
                        description={
                            preview.existing_parts_count > 0
                                ? `Elles seront ajoutées après les ${preview.existing_parts_count} partie(s) existante(s), qui ne sont pas modifiées.`
                                : "Les vidéos s'ajoutent ensuite partie par partie."
                        }
                    />
                    {preview.ignored_before > 0 && (
                        <Alert
                            className="mt-2"
                            type="warning"
                            showIcon
                            message={`${preview.ignored_before} paragraphe(s) avant le premier Titre 1 seront ignorés (page de garde…).`}
                        />
                    )}
                    <List
                        className="mt-4"
                        size="small"
                        bordered
                        dataSource={preview.parts}
                        style={{ maxHeight: 380, overflowY: "auto" }}
                        renderItem={(p, i) => (
                            <List.Item>
                                <List.Item.Meta
                                    title={<span>{(preview.existing_parts_count ?? 0) + i + 1}. {p.title}</span>}
                                    description={<span className="text-xs">{p.excerpt}{p.excerpt?.length >= 180 ? "…" : ""}</span>}
                                />
                                <div className="flex shrink-0 flex-col items-end gap-1">
                                    <Tag>{p.words} mots</Tag>
                                    {p.images > 0 && <Tag color="blue">{p.images} image{p.images > 1 ? "s" : ""}</Tag>}
                                </div>
                            </List.Item>
                        )}
                    />
                </>
            )}
        </Modal>
    );
};

export default ManualImport;
